import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DeleteImage, GetImages } from "../api/ImageApiCall";
import { ImageDataModel } from "../api/responsePayload/ImagesResponse";
import { showConsoleError } from "../util/ConsoleMessage";
import { setPagination } from "./pagination.slice";

interface ImagesState {
  items: ImageDataModel[];
  loading: boolean;
  error?: string;
}

const initialImagesState: ImagesState = { items: [], loading: false };

export const fetchImages = createAsyncThunk(
  "images/fetch",
  async (
    args: { endpoint?: string; id?: number; page?: number } | undefined,
    { dispatch, rejectWithValue }
  ) => {
    try {
      const res = await GetImages(args?.endpoint, args?.id, args?.page);
      if (res) {
        dispatch(
          setPagination({
            currentPage: res.current_page,
            nextPageUrl: res.next,
            prevPageUrl: res.previous,
            totalPages: res.total_pages,
            totalCount: res.count,
          })
        );
        return (res.results as ImageDataModel[]).map((v) => ({
          ...v,
          file: v.file,
        }));
      }
      return [] as ImageDataModel[];
    } catch (e: any) {
      showConsoleError("fetchImages error", e);
      return rejectWithValue(String(e));
    }
  }
);

export const deleteImage = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>("images/delete", async (imageId, { rejectWithValue }) => {
  try {
    await DeleteImage(imageId);
    return imageId;
  } catch (e: any) {
    showConsoleError("deleteImage error", e);
    return rejectWithValue(String(e));
  }
});

export const imagesSlice = createSlice({
  name: "images",
  initialState: initialImagesState,
  reducers: {
    setImages(state, action: PayloadAction<ImageDataModel[]>) {
      state.items = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchImages.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchImages.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchImages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setImages } = imagesSlice.actions;
export default imagesSlice.reducer;
